/**
 * The desktop's chat with the AI on this machine — a conversation like an IDE's assistant panel,
 * without MCP.
 *
 * Each turn runs the person's agent (Claude Code) in the repository's folder, in the same session
 * as the turns before (`--session-id` / `--resume`), and its output arrives as stream-json events:
 * text and tool use show as they happen.
 *
 * The AI reaches beyond the text box through **actions**: the first turn carries the catalog's
 * `tool.desktop-chat` instructions, which teach it to write `<vephus-action>{json}</vephus-action>`
 * blocks. The app shows each as a card; it runs only when the person confirms, through the same
 * API the screens use, and the result goes back to the AI as the next turn. The AI never gets the
 * person's session or keys.
 */

import { api } from './api'
import { findTeammate, type ChatAction } from './chatProtocol'

export * from './chatProtocol'

export interface ActionContext {
  repositoryId: string | null
  navigate: (route: string) => void
  /** Runs a catalog audit on the repository. */
  runAudit: (key: string) => Promise<string>
}

/**
 * Runs a confirmed action through the API, as the person, and says what happened in one line — the
 * line that goes back to the AI.
 */
export async function runAction(action: ChatAction, context: ActionContext): Promise<string> {
  switch (action.type) {
    case 'work_order': {
      const order = await api.workOrders.create({
        kind: action.kind,
        instructions: action.instructions,
        repositoryId: context.repositoryId,
        intervalMinutes: action.intervalMinutes ?? null,
      })
      return `Work order created (${order.kind}, id ${order.id}). It is running; the agent reports progress in Vephus.`
    }
    case 'team': {
      const teammates = await api.workOrders.teammates()
      const builder = findTeammate(teammates, action.builder)
      const reviewer = findTeammate(teammates, action.reviewer)
      if (action.builder && !builder) return `No teammate called "${action.builder}". Teammates: ${teammates.map((t) => t.name).join(', ')}.`
      if (action.reviewer && !reviewer) return `No teammate called "${action.reviewer}". Teammates: ${teammates.map((t) => t.name).join(', ')}.`
      const order = await api.workOrders.create({
        kind: 'Team',
        instructions: action.instructions,
        repositoryId: context.repositoryId,
        builderDeveloperId: builder?.developerId ?? null,
        reviewerDeveloperId: reviewer?.developerId ?? null,
      })
      return `Team work started (id ${order.id}): Builder ${builder?.name ?? 'the person'}, Reviewer ${reviewer?.name ?? builder?.name ?? 'the person'}.`
    }
    case 'queue': {
      const item = await api.promptQueue.send(action.instructions, context.repositoryId)
      return `Queued (id ${item.id}). The desktop app runs it within 30 seconds.`
    }
    case 'doc': {
      const doc = await api.docs.upsertByPath(action.path, action.content)
      return `Document saved: ${doc.path} (version ${doc.version}).`
    }
    case 'pin': {
      const doc = await api.docs.byPath(action.path)
      await api.docs.setPinned(doc.id, true)
      return `Pinned: ${doc.path}. Every new agent session starts with it.`
    }
    case 'audit':
      return context.runAudit(action.key)
    case 'open':
      context.navigate(action.route)
      return `Opened ${action.route}.`
  }
}
